import React, { useEffect, useState } from 'react'
import { User } from '../../assests/ExportImageIcon'
import Modal from '../modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
// Reuses hotelDetail.jsx's already-Figma-matching classes (reviewPart,
// reviewSummary, reviewBarRow, reviewCategory, reviewList, reviewCard) —
// same convention eventDetail.jsx already followed rather than duplicating
// the CSS into a new file.
import '../../pages/hotel/hotel-details/hotelDetail.css'

const PREVIEW_COUNT = 2
const MODAL_PAGE_SIZE = 10

const GoldStar = ({ filled = true }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 10.65 2.89 12.95 3.8 8.33.34 5.13l4.68-.55L7 .29l1.98 4.29 4.68.55-3.46 3.2.92 4.62Z" fill={filled ? '#F5A623' : '#E5E7EB'} />
    </svg>
)

const formatReviewDate = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// GET /bookings/rating-summary/ confirmed live shape:
// {count_1..count_5, avg_cleanliness, avg_service_rate, avg_location,
// avg_value_for_money, avg_rating, total_rating_count, content_type, object_id}
const CATEGORY_LABELS = [
    ['avg_cleanliness', 'Cleanliness'],
    ['avg_service_rate', 'Service'],
    ['avg_location', 'Location'],
    ['avg_value_for_money', 'Value for Money'],
]

// Backend sends these as fixed 2-decimal strings ("5.00", "4.67") — round to
// 1 decimal and drop a trailing ".0" so "5.00" reads as "5" and "4.50" as "4.5".
const formatRatingValue = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? Number(num.toFixed(1)).toString() : value
}

const buildSummary = (raw) => {
    const totalReviews = Number(raw?.total_rating_count || 0)
    const breakdown = [5, 4, 3, 2, 1].map((star) => {
        const count = Number(raw?.[`count_${star}`] || 0)
        const percent = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0
        const color = star >= 4 ? 'green' : star === 3 ? 'yellow' : 'red'
        return { star, count, percent, color }
    })
    const categories = CATEGORY_LABELS
        .map(([key, label]) => ({ label, value: raw?.[key] }))
        .filter((item) => item.value != null && item.value !== '')
        .map((item) => ({ ...item, value: formatRatingValue(item.value) }))
    return {
        average: raw?.avg_rating != null ? formatRatingValue(raw.avg_rating) : '—',
        totalReviews,
        breakdown,
        categories,
    }
}

// GET /bookings/rating/review confirmed live shape:
// {count, next, previous, data: {service, object_id, reviews: [{id, user_name,
// user_profile_photo, title, description, created_at, reply, rating}]}}
const mapReviewItem = (raw) => ({
    id: raw?.id,
    name: raw?.user_name || 'Guest',
    avatar: raw?.user_profile_photo || null,
    rating: Number(raw?.rating ?? 0),
    date: formatReviewDate(raw?.created_at),
    title: raw?.title || '',
    text: raw?.description || '',
})

const extractReviewList = (data) => {
    const list = data?.data?.reviews
    return Array.isArray(list) ? list.map(mapReviewItem) : []
}

const ReviewCard = ({ review }) => (
    <div className='reviewCard bgWhite commonBorder borderRadius20'>
        <div className='reviewCard__head'>
            <span className='reviewCard__avatar'>
                <img src={review.avatar || User} alt="" />
            </span>
            <div>
                <div className='reviewCard__name'>{review.name}</div>
                <div className='reviewCard__meta'>
                    <span className='reviewCard__stars'>
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                            <GoldStar key={starIndex} filled={starIndex < review.rating} />
                        ))}
                    </span>
                    {review.date && <span className='reviewCard__time'>{review.date}</span>}
                </div>
            </div>
        </div>
        {review.title ? <div className='reviewCard__name'>{review.title}</div> : null}
        <p className='reviewCard__text'>{review.text}</p>
    </div>
)

// Drop-in "Reviews & Ratings" section for any detail page — pass the
// backend's `service` value ("venue" | "hotel" | "event") and that record's
// own numeric id.
const ReviewsRatingSection = ({ service: serviceType, objectId }) => {
    const [summaryRaw, setSummaryRaw] = useState(null)
    const [loadingSummary, setLoadingSummary] = useState(true)
    const [previewReviews, setPreviewReviews] = useState([])
    const [modalOpen, setModalOpen] = useState(false)
    const [modalReviews, setModalReviews] = useState([])
    const [modalPage, setModalPage] = useState(1)
    const [modalHasMore, setModalHasMore] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)

    useEffect(() => {
        if (!serviceType || !objectId) return
        setLoadingSummary(true)
        service
            .get(endpoints.bookings.ratingSummary, { service: serviceType, object_id: objectId }, { silent: true })
            .then(({ data }) => setSummaryRaw(data?.data ?? null))
            // 404 "Rating stats not found" is a normal, expected state for a
            // record with zero reviews — not an error to surface.
            .catch(() => setSummaryRaw(null))
            .finally(() => setLoadingSummary(false))

        service
            .get(endpoints.bookings.reviewList, { service: serviceType, object_id: objectId, page: 1, page_size: PREVIEW_COUNT }, { silent: true })
            .then(({ data }) => setPreviewReviews(extractReviewList(data)))
            .catch(() => setPreviewReviews([]))
    }, [serviceType, objectId])

    const fetchModalPage = (page) => {
        setModalLoading(true)
        service
            .get(endpoints.bookings.reviewList, { service: serviceType, object_id: objectId, page, page_size: MODAL_PAGE_SIZE }, { silent: true })
            .then(({ data }) => {
                const list = extractReviewList(data)
                setModalReviews((prev) => (page === 1 ? list : [...prev, ...list]))
                setModalHasMore(Boolean(data?.next))
                setModalPage(page)
            })
            .catch(() => {})
            .finally(() => setModalLoading(false))
    }

    const openModal = () => {
        setModalOpen(true)
        if (modalReviews.length === 0) fetchModalPage(1)
    }

    // Nothing to show yet (still loading, or no service/id resolved) — avoid
    // a flash of an empty "Reviews & Ratings" section before data arrives.
    if (!serviceType || !objectId || loadingSummary) return null

    const summary = buildSummary(summaryRaw)
    const hasReviewContent = summary.totalReviews > 0

    return (
        <>
            <div className='reviewPart'>
                <div className='reviewPart__head'>
                    <div className='bsSectionTitle'>Reviews & Ratings</div>
                    {hasReviewContent && (
                        <button type='button' className='hotelRoomCard__viewAll' onClick={openModal}>View All</button>
                    )}
                </div>

                <div className='reviewPart__grid'>
                    <div className='reviewSummary bgWhite commonBorder borderRadius20'>
                        <div className='reviewSummary__scoreRow'>
                            <div className='reviewSummary__score'>{summary.average}</div>
                            <div>
                                <div className='reviewSummary__stars'>
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <GoldStar key={index} filled={index < Math.round(Number(summary.average) || 0)} />
                                    ))}
                                </div>
                                <div className='reviewSummary__count'>
                                    {hasReviewContent
                                        ? `${summary.totalReviews.toLocaleString('en-IN')} reviews`
                                        : 'No reviews yet'}
                                </div>
                            </div>
                        </div>

                        {hasReviewContent ? (
                            <div className='reviewSummary__bars'>
                                {summary.breakdown.map((row) => (
                                    <div className='reviewBarRow' key={row.star}>
                                        <span className={`reviewBarRow__label reviewBarRow__label--${row.color}`}>{row.star}★</span>
                                        <span className='reviewBarRow__track'>
                                            <span className={`reviewBarRow__fill reviewBarRow__fill--${row.color}`} style={{ width: `${row.percent}%` }} />
                                        </span>
                                        <span className='reviewBarRow__count'>{row.count}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {summary.categories.length ? (
                            <>
                                <div className='reviewSummary__divider' />
                                <div className='reviewSummary__categories'>
                                    {summary.categories.map((category) => (
                                        <div className='reviewCategory' key={category.label}>
                                            <span className='reviewCategory__label'>{category.label}</span>
                                            <span className='reviewCategory__value'>{category.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : null}
                    </div>

                    <div className='reviewList'>
                        {previewReviews.length ? previewReviews.map((review) => (
                            <ReviewCard review={review} key={review.id} />
                        )) : (
                            <div className='reviewCard bgWhite commonBorder borderRadius20'>
                                <p className='reviewCard__text'>No guest reviews available yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)} maxWidth={640} className='policyModal'>
                <div className='bsSectionTitle mb-3'>All Reviews</div>
                <div className='reviewList'>
                    {modalLoading && modalReviews.length === 0 ? (
                        <p className='bsFareNote'>Loading reviews...</p>
                    ) : modalReviews.length ? (
                        modalReviews.map((review) => <ReviewCard review={review} key={review.id} />)
                    ) : (
                        <p className='bsFareNote'>No guest reviews available yet.</p>
                    )}
                </div>
                {modalHasMore && (
                    <button
                        type='button'
                        className='travellerAddBtn w-100 mt-3'
                        disabled={modalLoading}
                        onClick={() => fetchModalPage(modalPage + 1)}
                    >
                        {modalLoading ? 'Loading...' : 'Load More'}
                    </button>
                )}
            </Modal>
        </>
    )
}

export default ReviewsRatingSection
